import { useEffect, useRef } from 'react';
import { useApp } from '@/contexts/AppContext';
import { useAuth } from '@/contexts/AuthContext';

declare global {
  interface Window {
    troiaWidgetConfig?: {
      channelId: string;
      token: string;
      apiUrl: string;
      socketUrl: string;
      hidden?: boolean;
      visitorName?: string;
      visitorEmail?: string;
      visitorPhone?: string;
      visitorTags?: string[];
      company?: {
        name: string;
        type?: 'PF' | 'PJ';
        document?: string;
        email?: string;
        phone?: string;
      };
    };
    TroiaWidget?: {
      open: () => void;
      close: () => void;
      send: (message: string) => void;
    };
  }
}

export const SupportWidget: React.FC = () => {
  const { app } = useApp();
  const { state } = useAuth();
  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const initializedRef = useRef(false);

  const user = state.user;

  useEffect(() => {
    if (!app?.support?.widget || !app?.widgetUrl || !app?.backendUrl) {
      return;
    }

    // Prevent double initialization (React StrictMode)
    if (initializedRef.current) {
      return;
    }
    initializedRef.current = true;

    const { channelId, token } = app.support.widget;
    const backendUrl = app.backendUrl.startsWith('http') ? app.backendUrl : `https://${app.backendUrl}`;

    // Configure widget with logged-in user data
    window.troiaWidgetConfig = {
      channelId,
      token,
      apiUrl: backendUrl,
      socketUrl: backendUrl,
      hidden: true,
      visitorName: user ? `${user.firstName} ${user.lastName}`.trim() : undefined,
      visitorEmail: user?.email,
      visitorPhone: user?.phone,
    };

    // Inject widget script
    const script = document.createElement('script');
    const widgetBaseUrl = app.widgetUrl.startsWith('http') ? app.widgetUrl : `https://${app.widgetUrl}`;
    script.src = `${widgetBaseUrl}/widget.umd.js`;
    script.async = true;
    document.body.appendChild(script);
    scriptRef.current = script;

    return () => {
      // Cleanup
      if (scriptRef.current) {
        document.body.removeChild(scriptRef.current);
        scriptRef.current = null;
      }
      delete window.troiaWidgetConfig;
      delete window.TroiaWidget;
      initializedRef.current = false;

      // Remove widget container if it exists
      const container = document.getElementById('troia-widget-container');
      if (container) {
        container.remove();
      }
    };
  }, [app?.support?.widget, app?.widgetUrl, app?.backendUrl]);

  return null;
};
